'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuctionItem } from '@/app/components/AuctionDetailModal';
import { arrematacoesApi, ApiError, type StatusArrematado as BeStatusArrematado } from '@/lib/api';
import type { BeArrematacao, CategoriaCusto as BeCategoria, BeCusto } from '@/lib/api';
import { beAuctionToAuctionItem } from '@/lib/api/adapters';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'metricaleiloes-arrematacoes';

export type StatusArrematado = 'Ocupado' | 'Desocupado' | 'Em Reforma' | 'À Venda' | 'Vendido';
export type CategoriaCusto =
  | 'Reforma'
  | 'Impostos'
  | 'Taxas Cartório'
  | 'Honorários Jurídicos'
  | 'ITBI'
  | 'Condomínio'
  | 'IPTU'
  | 'Comissão Venda'
  | 'Outros';

export interface Custo {
  id: string;
  beId?: string;
  descricao: string;
  categoria: CategoriaCusto;
  valor: number;
  data: string;
}

export interface Arrematacao {
  id: string;
  beId?: string;
  auction: AuctionItem;
  valorArrematacao: number;
  dataArrematacao: string;
  status: StatusArrematado;
  custos: Custo[];
  valorVenda?: number;
  comissaoPercent: number;
  observacoes?: string;
  criadoEm: string;
}

interface ArrematacoesContextValue {
  arrematacoes: Arrematacao[];
  isArrematado: (id: string) => boolean;
  addArrematacao: (
    auction: AuctionItem,
    valorArrematacao: number,
    dataArrematacao: string
  ) => Promise<void>;
  updateArrematacao: (
    id: string,
    updates: Partial<Omit<Arrematacao, 'id' | 'auction'>>
  ) => Promise<void>;
  removeArrematacao: (id: string) => Promise<void>;
  addCusto: (arrematacaoId: string, custo: Omit<Custo, 'id'>) => Promise<void>;
  updateCusto: (
    arrematacaoId: string,
    custoId: string,
    updates: Partial<Omit<Custo, 'id'>>
  ) => void;
  removeCusto: (arrematacaoId: string, custoId: string) => Promise<void>;
  count: number;
  isSynced: boolean;
}

const ArrematacoesContext = createContext<ArrematacoesContextValue | null>(null);

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Mapeamento de status FE ↔ BE ───────────────────────────────────────────
const STATUS_TO_BE: Record<StatusArrematado, string> = {
  Ocupado: 'OCUPADO',
  Desocupado: 'DESOCUPADO',
  'Em Reforma': 'EM_REFORMA',
  'À Venda': 'A_VENDA',
  Vendido: 'VENDIDO',
};
const STATUS_FROM_BE: Record<string, StatusArrematado> = {
  OCUPADO: 'Ocupado',
  DESOCUPADO: 'Desocupado',
  EM_REFORMA: 'Em Reforma',
  A_VENDA: 'À Venda',
  VENDIDO: 'Vendido',
};
const CAT_TO_BE: Record<CategoriaCusto, BeCategoria> = {
  ITBI: 'ITBI',
  Impostos: 'IPTU',
  'Taxas Cartório': 'REGISTRO',
  'Honorários Jurídicos': 'ADVOCACIA',
  Reforma: 'REFORMA',
  Condomínio: 'CONDOMINIO',
  IPTU: 'IPTU',
  'Comissão Venda': 'LEILOEIRO',
  Outros: 'OUTROS',
};

const BE_CATEGORY_CODE_TO_FE: Record<string, CategoriaCusto> = {
  ITBI: 'ITBI',
  REGISTRO: 'Taxas Cartório',
  ADVOCACIA: 'Honorários Jurídicos',
  REFORMA: 'Reforma',
  CONDOMINIO: 'Condomínio',
  IPTU: 'IPTU',
  DESOCUPACAO: 'Outros',
  LEILOEIRO: 'Comissão Venda',
  FINANCIAMENTO: 'Outros',
  OUTROS: 'Outros',
};

function custoCategoriaFromBe(c: BeCusto): CategoriaCusto {
  const code = c.category?.code ?? c.categoria;
  if (!code) return 'Outros';
  return BE_CATEGORY_CODE_TO_FE[code] ?? 'Outros';
}

function beToLocal(be: BeArrematacao, auctionItem: AuctionItem): Arrematacao {
  return {
    id: `be-${be.id}`,
    beId: be.id,
    auction: auctionItem,
    valorArrematacao: be.valorArrematado,
    dataArrematacao: be.dataArrematacao,
    status: STATUS_FROM_BE[be.status] ?? 'Desocupado',
    custos: be.custos.map((c) => ({
      id: `be-${c.id}`,
      beId: c.id,
      descricao: c.descricao,
      categoria: custoCategoriaFromBe(c),
      valor: c.valor,
      data: c.data,
    })),
    valorVenda: be.venda?.valorVenda,
    comissaoPercent: be.venda?.comissao ?? 6,
    criadoEm: be.createdAt,
  };
}

function localLoad(): Arrematacao[] {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : [];
  } catch {
    return [];
  }
}
function localSave(items: Arrematacao[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* noop */
  }
}

export function ArrematacoesProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isOnline } = useAuth();
  const [arrematacoes, setArrematacoes] = useState<Arrematacao[]>([]);
  const [isSynced, setIsSynced] = useState(false);

  useEffect(() => {
    if (isAuthenticated && isOnline) {
      arrematacoesApi
        .list()
        .then((beList) => {
          const items = beList.map((be) => beToLocal(be, beAuctionToAuctionItem(be.auction)));
          setArrematacoes(items);
          localSave(items);
          setIsSynced(true);
        })
        .catch(() => {
          setArrematacoes(localLoad());
        });
    } else {
      setArrematacoes(localLoad());
      setIsSynced(false);
    }
  }, [isAuthenticated, isOnline]);

  const persist = (items: Arrematacao[]) => {
    setArrematacoes(items);
    localSave(items);
  };

  const isArrematado = useCallback(
    (id: string) => arrematacoes.some((a) => a.auction.id === id),
    [arrematacoes]
  );

  const addArrematacao = useCallback(
    async (auction: AuctionItem, valorArrematacao: number, dataArrematacao: string) => {
      if (arrematacoes.some((a) => a.auction.id === auction.id)) return;

      if (isAuthenticated && isOnline && auction.beId) {
        try {
          const be = await arrematacoesApi.create({
            opportunityId: auction.beId,
            valorArrematado: valorArrematacao,
            dataArrematacao,
            status: 'DESOCUPADO',
          });
          persist([beToLocal(be, auction), ...arrematacoes]);
          return;
        } catch (e) {
          if (!(e instanceof ApiError)) throw e;
        }
      }

      // Fallback local
      const nova: Arrematacao = {
        id: uid(),
        auction,
        valorArrematacao,
        dataArrematacao,
        status: 'Desocupado',
        custos: [],
        comissaoPercent: 6,
        criadoEm: new Date().toISOString(),
      };
      persist([nova, ...arrematacoes]);
    },
    [arrematacoes, isAuthenticated, isOnline]
  );

  const updateArrematacao = useCallback(
    async (id: string, updates: Partial<Omit<Arrematacao, 'id' | 'auction'>>) => {
      const item = arrematacoes.find((a) => a.id === id);
      if (!item) return;
      const next = arrematacoes.map((a) => (a.id === id ? { ...a, ...updates } : a));
      persist(next);

      if (isAuthenticated && isOnline && item.beId) {
        try {
          await arrematacoesApi.update(item.beId, {
            ...(updates.status !== undefined && {
              status: STATUS_TO_BE[updates.status] as BeStatusArrematado,
            }),
            ...(updates.observacoes !== undefined && { observacoes: updates.observacoes }),
            ...(updates.valorArrematacao !== undefined && {
              valorArrematado: updates.valorArrematacao,
            }),
          });
          if (updates.valorVenda !== undefined && item.beId) {
            await arrematacoesApi.upsertVenda(item.beId, {
              valorVenda: updates.valorVenda,
              comissao: updates.comissaoPercent ?? item.comissaoPercent,
            });
          }
        } catch {
          /* mantém local */
        }
      }
    },
    [arrematacoes, isAuthenticated, isOnline]
  );

  const removeArrematacao = useCallback(
    async (id: string) => {
      const item = arrematacoes.find((a) => a.id === id);
      persist(arrematacoes.filter((a) => a.id !== id));
      if (isAuthenticated && isOnline && item?.beId) {
        try {
          await arrematacoesApi.remove(item.beId);
        } catch {
          /* noop */
        }
      }
    },
    [arrematacoes, isAuthenticated, isOnline]
  );

  const addCusto = useCallback(
    async (arrematacaoId: string, custo: Omit<Custo, 'id'>) => {
      const item = arrematacoes.find((a) => a.id === arrematacaoId);
      if (!item) return;

      if (isAuthenticated && isOnline && item.beId) {
        try {
          const beCusto = await arrematacoesApi.addCusto(item.beId, {
            categoria: CAT_TO_BE[custo.categoria] ?? 'OUTROS',
            descricao: custo.descricao,
            valor: custo.valor,
            data: custo.data,
          });
          persist(
            arrematacoes.map((a) =>
              a.id === arrematacaoId
                ? {
                    ...a,
                    custos: [...a.custos, { ...custo, id: `be-${beCusto.id}`, beId: beCusto.id }],
                  }
                : a
            )
          );
          return;
        } catch {
          /* fallback local */
        }
      }
      persist(
        arrematacoes.map((a) =>
          a.id === arrematacaoId ? { ...a, custos: [...a.custos, { ...custo, id: uid() }] } : a
        )
      );
    },
    [arrematacoes, isAuthenticated, isOnline]
  );

  const updateCusto = useCallback(
    (arrId: string, custoId: string, updates: Partial<Omit<Custo, 'id'>>) => {
      persist(
        arrematacoes.map((a) =>
          a.id === arrId
            ? { ...a, custos: a.custos.map((c) => (c.id === custoId ? { ...c, ...updates } : c)) }
            : a
        )
      );
    },
    [arrematacoes]
  );

  const removeCusto = useCallback(
    async (arrId: string, custoId: string) => {
      const item = arrematacoes.find((a) => a.id === arrId);
      const custo = item?.custos.find((c) => c.id === custoId);
      persist(
        arrematacoes.map((a) =>
          a.id === arrId ? { ...a, custos: a.custos.filter((c) => c.id !== custoId) } : a
        )
      );
      if (isAuthenticated && isOnline && item?.beId && custo?.beId) {
        try {
          await arrematacoesApi.removeCusto(item.beId, custo.beId);
        } catch {
          /* noop */
        }
      }
    },
    [arrematacoes, isAuthenticated, isOnline]
  );

  return (
    <ArrematacoesContext.Provider
      value={{
        arrematacoes,
        isArrematado,
        addArrematacao,
        updateArrematacao,
        removeArrematacao,
        addCusto,
        updateCusto,
        removeCusto,
        count: arrematacoes.length,
        isSynced,
      }}
    >
      {children}
    </ArrematacoesContext.Provider>
  );
}

export function useArrematacoes() {
  const ctx = useContext(ArrematacoesContext);
  if (!ctx) throw new Error('useArrematacoes must be used inside ArrematacoesProvider');
  return ctx;
}
